import React, { useState } from 'react';
import { Plus, Trash2, AlertTriangle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface CustomField {
  id: string;
  entity_type: 'customer' | 'task' | 'work_order' | 'invoice';
  field_name: string;
  field_type: 'text' | 'number' | 'date' | 'boolean' | 'select';
  field_label: string;
  required: boolean;
  options?: string[];
}

const CustomFieldsTab = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<CustomField[]>([]);
  const [selectedEntity, setSelectedEntity] = useState<CustomField['entity_type']>('customer');

  // Load custom fields
  React.useEffect(() => {
    const loadFields = async () => {
      try {
        const { data, error } = await supabase
          .from('custom_fields')
          .select('*')
          .order('created_at');

        if (error) throw error;
        setFields(data || []);
      } catch (err) {
        console.error('Error loading custom fields:', err);
        setError('Failed to load custom fields');
      }
    };

    loadFields();
  }, []);

  const handleAddField = async () => {
    try {
      setLoading(true);
      setError(null);

      // Generate a safe field name
      const baseFieldName = 'custom_field';
      let fieldName = baseFieldName;
      let counter = 1;

      while (fields.some(f => f.field_name === fieldName)) {
        fieldName = `${baseFieldName}_${counter}`;
        counter++;
      }

      const { data, error } = await supabase
        .from('custom_fields')
        .insert({
          entity_type: selectedEntity,
          field_name: fieldName,
          field_type: 'text',
          field_label: 'New Field',
          required: false
        })
        .select()
        .single();

      if (error) throw error;
      setFields(prev => [...prev, data]);
    } catch (err) {
      console.error('Error adding custom field:', err);
      setError('Failed to add custom field');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateField = async (id: string, updates: Partial<CustomField>) => {
    try {
      setLoading(true);
      setError(null);

      const { error } = await supabase
        .from('custom_fields')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      setFields(prev => 
        prev.map(field => 
          field.id === id ? { ...field, ...updates } : field
        )
      );
    } catch (err) {
      console.error('Error updating custom field:', err);
      setError('Failed to update custom field');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteField = async (id: string) => {
    try {
      setLoading(true);
      setError(null);

      const { error } = await supabase
        .from('custom_fields')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setFields(prev => prev.filter(field => field.id !== id));
    } catch (err) {
      console.error('Error deleting custom field:', err);
      setError('Failed to delete custom field');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-medium">Custom Fields</h2>
          <p className="text-muted-foreground">Add custom fields to collect additional information</p>
        </div>
        <div className="flex items-center space-x-4">
          <Select
            value={selectedEntity}
            onValueChange={(value) => setSelectedEntity(value as CustomField['entity_type'])}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Select entity type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="customer">Customers</SelectItem>
              <SelectItem value="task">Tasks</SelectItem>
              <SelectItem value="work_order">Work Orders</SelectItem>
              <SelectItem value="invoice">Invoices</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={handleAddField} disabled={loading}>
            <Plus className="w-5 h-5 mr-2" />
            Add Field
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="w-5 h-5" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-4">
        {fields
          .filter(field => field.entity_type === selectedEntity)
          .map(field => (
            <Card key={field.id}>
              <CardHeader className="pb-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="space-y-2">
                    <Label>Field Label</Label>
                    <Input
                      value={field.field_label}
                      onChange={(e) => handleUpdateField(field.id, { field_label: e.target.value })}
                      placeholder="Enter field label"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Field Type</Label>
                    <Select
                      value={field.field_type}
                      onValueChange={(value) => handleUpdateField(field.id, { 
                        field_type: value as CustomField['field_type'],
                        options: value === 'select' ? [] : undefined
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select field type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="text">Text</SelectItem>
                        <SelectItem value="number">Number</SelectItem>
                        <SelectItem value="date">Date</SelectItem>
                        <SelectItem value="boolean">Yes/No</SelectItem>
                        <SelectItem value="select">Select</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {field.field_type === 'select' && (
                    <div className="space-y-2">
                      <Label>Options (one per line)</Label>
                      <Textarea
                        value={field.options?.join('\n') || ''}
                        onChange={(e) => handleUpdateField(field.id, { 
                          options: e.target.value.split('\n').filter(Boolean)
                        })}
                        rows={3}
                        placeholder="Enter options..."
                      />
                    </div>
                  )}

                  <div className="flex items-center justify-between space-x-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`required-${field.id}`}
                        checked={field.required}
                        onCheckedChange={(checked) => 
                          handleUpdateField(field.id, { required: checked as boolean })
                        }
                      />
                      <Label htmlFor={`required-${field.id}`}>Required</Label>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        if (window.confirm('Are you sure you want to delete this field?')) {
                          handleDeleteField(field.id);
                        }
                      }}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
            </Card>
          ))}

        {fields.filter(field => field.entity_type === selectedEntity).length === 0 && (
          <Card>
            <CardContent className="text-center py-12">
              <CardTitle className="text-sm font-medium text-muted-foreground">No custom fields yet</CardTitle>
              <CardDescription className="mt-1">
                Get started by adding a custom field for {selectedEntity}s
              </CardDescription>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default CustomFieldsTab;